import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  FileText,
  Plus,
  X,
  Trash2,
  AlertCircle,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";

const MAX_DESCRIPTION_LENGTH = 200;

/**
 * Event Schedule Section Component
 * Dynamic list where users can add agenda items with title, time, and description
 */
const EventScheduleSection = ({
  value = [],
  onChange,
  className = "",
}) => {
  const [agendaItems, setAgendaItems] = useState(value || []);
  const [newAgenda, setNewAgenda] = useState({
    title: "",
    time: "",
    description: "",
  });

  // Update agenda items when value prop changes
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setAgendaItems(value);
    }
  }, [value]);

  const handleInputChange = (field, value) => {
    if (field === "description" && value.length > MAX_DESCRIPTION_LENGTH) {
      toast.warning(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }
    setNewAgenda((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddAgenda = () => {
    const trimmedTitle = newAgenda.title.trim();
    const trimmedDescription = newAgenda.description.trim();

    if (!trimmedTitle) {
      toast.error("Please enter an agenda title");
      return;
    }

    if (!newAgenda.time) {
      toast.error("Please select an agenda time");
      return;
    }

    if (!trimmedDescription) {
      toast.error("Please enter an agenda description");
      return;
    }

    if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
      toast.error(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }

    const newItem = {
      title: trimmedTitle,
      time: newAgenda.time,
      description: trimmedDescription,
    };

    const updatedAgenda = [...agendaItems, newItem];
    setAgendaItems(updatedAgenda);
    onChange(updatedAgenda);
    setNewAgenda({
      title: "",
      time: "",
      description: "",
    });
    toast.success("Agenda item added");
  };

  const handleRemoveAgenda = (index) => {
    const updatedAgenda = agendaItems.filter((_, i) => i !== index);
    setAgendaItems(updatedAgenda);
    onChange(updatedAgenda);
    toast.success("Agenda item removed");
  };

  const handleUpdateAgenda = (index, field, value) => {
    if (field === "description" && value.length > MAX_DESCRIPTION_LENGTH) {
      toast.warning(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }
    const updatedAgenda = [...agendaItems];
    updatedAgenda[index][field] = value;
    setAgendaItems(updatedAgenda);
    onChange(updatedAgenda);
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Event Schedule</h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
            {agendaItems.length}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Add agenda items for your event schedule. Each item includes a title,
          time, and short description (max {MAX_DESCRIPTION_LENGTH} characters).
          You can add multiple agenda items.
        </p>

        {/* Add Agenda Form */}
        <div className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            {/* Agenda Title */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Agenda Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={newAgenda.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="e.g., Opening Ceremony, Keynote Speech"
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Agenda Time */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Agenda Time <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="time"
                  value={newAgenda.time}
                  onChange={(e) => handleInputChange("time", e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Agenda Description */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Short Description <span className="text-red-500">*</span>
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Max {MAX_DESCRIPTION_LENGTH} characters)
                </span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={newAgenda.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Brief description of this agenda item..."
                  rows={3}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white resize-y"
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                <p
                  className={`text-xs ${
                    newAgenda.description.length > MAX_DESCRIPTION_LENGTH * 0.9
                      ? "text-orange-600"
                      : newAgenda.description.length === MAX_DESCRIPTION_LENGTH
                      ? "text-red-600"
                      : "text-gray-500"
                  }`}
                >
                  {newAgenda.description.length}/{MAX_DESCRIPTION_LENGTH} characters
                </p>
              </div>
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={handleAddAgenda}
              disabled={
                !newAgenda.title.trim() ||
                !newAgenda.time ||
                !newAgenda.description.trim()
              }
              className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Agenda Item</span>
            </button>
          </div>
        </div>

        {/* Agenda Items List */}
        {agendaItems.length > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-700">
              Agenda Items ({agendaItems.length}):
            </p>
            <div className="space-y-3">
              {agendaItems.map((item, index) => (
                <div
                  key={index}
                  className="p-4 bg-purple-50 border border-purple-200 rounded-lg group hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-purple-600 rounded-full">
                          <span className="text-xs font-bold text-white">
                            {index + 1}
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-gray-900">
                          {item.title}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-4 ml-10 mb-2">
                        <div className="flex items-center space-x-1.5 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-purple-600" />
                          <span className="font-medium">{item.time}</span>
                        </div>
                      </div>
                      <p className="ml-10 text-xs text-gray-700 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAgenda(index)}
                      className="flex-shrink-0 p-2 ml-3 text-red-600 bg-white rounded-lg border border-red-200 transition-colors hover:bg-red-50 hover:border-red-300"
                      title="Remove agenda item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              No agenda items added yet
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Start adding agenda items using the form above
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventScheduleSection;

